import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { workflowScheduleKeys } from '~/lib/query-keys';

export interface WorkflowSchedule {
  triggerId: string;
  workflowId: string;
  workflowTitle: string;
  /** Raw five-field cron expression — the workflow service stores no friendlier form. */
  expression?: string;
  timezone?: string;
  enabled: boolean;
  /** The caller created this workflow, so the unattended-approval switch is theirs. */
  ownedByCaller: boolean;
  /** The caller approved this workflow to run tier-3 tools without asking (FRONT-1811). */
  autoApproved: boolean;
}

// One stable empty list: a fresh `[]` per render would re-run every memo that
// depends on `schedules` (the command palette's selection pin among them).
const NO_SCHEDULES: WorkflowSchedule[] = [];

/**
 * Workflows fire on their own schedule too, so the Schedule page would be lying
 * by omission without them. One request: the server reads the workflows and
 * their cron triggers (`src/workflow/schedule-routes.ts`).
 */
export function useWorkflowSchedules(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: workflowScheduleKeys.list(),
    enabled: options?.enabled,
    queryFn: async () => {
      const data = await api.get<{ schedules?: WorkflowSchedule[] }>('/api/workflow/schedules');
      return data.schedules ?? [];
    },
    // The platform service may be unreachable or unconfigured for this
    // deployment. The rest of the page must still render, so a failure here
    // stays a failure of this section alone.
    retry: false,
  });

  return { schedules: query.data ?? NO_SCHEDULES, isLoading: query.isLoading };
}
